import { logger } from '../utils/logger';

/**
 * Offline Queue Service
 *
 * Queues failed API requests when the user is offline, persists them
 * to localStorage, and auto-retries with exponential backoff when
 * connectivity is restored.
 */

export interface QueuedRequest {
  id: string;
  method: 'POST' | 'PUT' | 'DELETE';
  url: string;
  data?: unknown;
  headers?: Record<string, string>;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

export type QueueChangeCallback = (queue: QueuedRequest[]) => void;

const STORAGE_KEY = 'vitly_offline_queue';
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

class OfflineQueueService {
  private queue: QueuedRequest[] = [];
  private listeners: Set<QueueChangeCallback> = new Set();
  private processing = false;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.loadFromStorage();
    this.setupEventListeners();
  }

  /** Add a failed request to the queue. */
  enqueue(request: Omit<QueuedRequest, 'id' | 'timestamp' | 'retryCount' | 'maxRetries'> & { maxRetries?: number }): void {
    const queuedRequest: QueuedRequest = {
      ...request,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: request.maxRetries ?? 3,
    };

    this.queue.push(queuedRequest);
    this.saveToStorage();
    this.notifyListeners();
    logger.info('[OfflineQueue] Enqueued request', {
      id: queuedRequest.id,
      method: queuedRequest.method,
      url: queuedRequest.url,
    });
  }

  /** Process all queued requests. Returns the number of successfully processed items. */
  async processQueue(): Promise<number> {
    if (this.processing) {
      logger.debug('[OfflineQueue] Already processing, skipping');
      return 0;
    }

    if (this.queue.length === 0) {
      return 0;
    }

    if (!navigator.onLine) {
      logger.debug('[OfflineQueue] Still offline, skipping process');
      return 0;
    }

    this.processing = true;
    this.notifyListeners();
    let successCount = 0;
    const failedItems: QueuedRequest[] = [];

    for (const request of [...this.queue]) {
      try {
        await this.executeRequest(request);
        successCount++;
        logger.info('[OfflineQueue] Successfully processed', {
          id: request.id,
          method: request.method,
          url: request.url,
        });
      } catch (_error) {
        request.retryCount++;
        if (request.retryCount < request.maxRetries) {
          failedItems.push(request);
          logger.warn('[OfflineQueue] Request failed, will retry', {
            id: request.id,
            retryCount: request.retryCount,
            maxRetries: request.maxRetries,
          });
        } else {
          logger.error('[OfflineQueue] Request exceeded max retries, dropping', {
            id: request.id,
            method: request.method,
            url: request.url,
          });
        }
      }
    }

    this.queue = failedItems;
    this.processing = false;
    this.saveToStorage();
    this.notifyListeners();

    // If there are still failed items, schedule a retry with backoff
    if (failedItems.length > 0 && navigator.onLine) {
      this.scheduleRetry(failedItems[0].retryCount);
    }

    return successCount;
  }

  /** Return the number of pending requests. */
  getQueueSize(): number {
    return this.queue.length;
  }

  /** Return whether the queue is currently being processed. */
  isProcessing(): boolean {
    return this.processing;
  }

  /** Return a copy of the current queue. */
  getQueue(): QueuedRequest[] {
    return [...this.queue];
  }

  /** Remove a specific item from the queue. */
  removeItem(id: string): void {
    this.queue = this.queue.filter((item) => item.id !== id);
    this.saveToStorage();
    this.notifyListeners();
  }

  /** Clear all queued requests. */
  clearQueue(): void {
    this.queue = [];
    this.saveToStorage();
    this.notifyListeners();
    logger.info('[OfflineQueue] Queue cleared');
  }

  /** Subscribe to queue changes. Returns an unsubscribe function. */
  onQueueChange(callback: QueueChangeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async executeRequest(request: QueuedRequest): Promise<void> {
    const { method, url, data, headers } = request;

    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      credentials: 'include', // Match withCredentials: true from the axios client
    };

    if (data !== undefined && method !== 'DELETE') {
      fetchOptions.body = JSON.stringify(data);
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  private scheduleRetry(retryCount: number): void {
    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
    }

    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
    logger.debug('[OfflineQueue] Scheduling retry in', delay, 'ms');

    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      this.processQueue();
    }, delay);
  }

  private setupEventListeners(): void {
    window.addEventListener('online', this.handleOnline);
  }

  private handleOnline = (): void => {
    logger.info('[OfflineQueue] Back online, processing queue');
    // Small delay to let the network stabilize
    setTimeout(() => {
      this.processQueue();
    }, 500);
  };

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this.queue = parsed;
          logger.debug('[OfflineQueue] Loaded', this.queue.length, 'items from storage');
        }
      }
    } catch (error) {
      logger.error('[OfflineQueue] Failed to load from storage', error);
      this.queue = [];
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      logger.error('[OfflineQueue] Failed to save to storage', error);
    }
  }

  private notifyListeners(): void {
    const snapshot = this.getQueue();
    this.listeners.forEach((callback) => {
      try {
        callback(snapshot);
      } catch (error) {
        logger.error('[OfflineQueue] Listener error', error);
      }
    });
  }
}

/** Singleton instance */
export const offlineQueueService = new OfflineQueueService();

export default offlineQueueService;
